import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Search } from "lucide-react";

const API = '/api';

const CATEGORIAS = ["Linha Clínica", "Linha Farmácia", "Geral"];

export default function Produtos() {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoria, setSelectedCategoria] = useState("all");
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    codigo: "",
    descricao: "",
    categoria: "Linha Clínica",
    pva: "",
  });

  const fetchProdutos = async () => {
    try {
      const response = await axios.get(`${API}/produtos`);
      setProdutos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProdutos();
  }, []);

  const resetForm = () => {
    setFormData({
      codigo: "",
      descricao: "",
      categoria: "Linha Clínica",
      pva: "",
    });
    setEditingProduct(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (produto) => {
    setEditingProduct(produto);
    setFormData({
      codigo: produto.codigo,
      descricao: produto.descricao,
      categoria: produto.categoria,
      pva: produto.pva.toString(),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.codigo || !formData.descricao || !formData.categoria || !formData.pva) {
      toast.error("Preencha todos os campos");
      return;
    }

    const pva = parseFloat(formData.pva);
    if (isNaN(pva) || pva < 0) {
      toast.error("PVA deve ser um número válido");
      return;
    }

    try {
      const payload = {
        codigo: formData.codigo,
        descricao: formData.descricao,
        categoria: formData.categoria,
        pva: pva,
      };

      if (editingProduct) {
        await axios.put(`${API}/produtos/${editingProduct.id}`, payload);
        toast.success("Produto atualizado com sucesso");
      } else {
        await axios.post(`${API}/produtos`, payload);
        toast.success("Produto criado com sucesso");
      }

      setDialogOpen(false);
      resetForm();
      fetchProdutos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar produto");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/produtos/${id}`);
      toast.success("Produto eliminado");
      fetchProdutos();
    } catch (error) {
      toast.error("Erro ao eliminar produto");
    }
  };

  const filteredProdutos = produtos.filter((p) => {
    const matchesSearch = 
      p.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.codigo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = selectedCategoria === "all" || p.categoria === selectedCategoria;
    return matchesSearch && matchesCategoria;
  });

  const produtosByCategoria = CATEGORIAS.reduce((acc, cat) => {
    acc[cat] = filteredProdutos.filter((p) => p.categoria === cat);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="produtos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-slate-900">
            Produtos
          </h1>
          <p className="text-slate-500 mt-1">
            Gestão de produtos ({produtos.length} total)
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              onClick={openCreateDialog}
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4" />
              Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingProduct ? "Editar Produto" : "Novo Produto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">Código CNP</Label>
                <Input
                  id="codigo"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Ex: 7530386"
                  data-testid="input-codigo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="descricao">Designação</Label>
                <Input
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: ALIVIOGEL 100ML"
                  data-testid="input-descricao"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pva">PVA (€)</Label>
                <Input
                  id="pva"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.pva}
                  onChange={(e) => setFormData({ ...formData, pva: e.target.value })}
                  placeholder="Ex: 12.10"
                  data-testid="input-pva"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-product-btn"
                >
                  {editingProduct ? "Guardar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Pesquisar por nome ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-produtos"
          />
        </div>
        <Select value={selectedCategoria} onValueChange={setSelectedCategoria}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-categoria">
            <SelectValue placeholder="Filtrar categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {CATEGORIAS.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products by Category */}
      <Card>
        <Tabs defaultValue="Linha Clínica">
          <CardHeader className="border-b border-slate-100 pb-0">
            <TabsList className="w-full justify-start gap-2 bg-transparent p-0 flex-wrap">
              {CATEGORIAS.map((cat) => (
                <TabsTrigger
                  key={cat}
                  value={cat}
                  className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 rounded-t-lg rounded-b-none border-b-2 border-transparent data-[state=active]:border-blue-600 px-4 py-2"
                  data-testid={`tab-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                  <Badge variant="secondary" className="ml-2 bg-slate-100">
                    {produtosByCategoria[cat]?.length || 0}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </CardHeader>

          {CATEGORIAS.map((cat) => (
            <TabsContent key={cat} value={cat} className="mt-0">
              <CardContent className="p-0">
                {produtosByCategoria[cat]?.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhum produto nesta categoria</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/80">
                          <TableHead className="font-bold text-slate-700">Código</TableHead>
                          <TableHead className="font-bold text-slate-700">Designação</TableHead>
                          <TableHead className="font-bold text-slate-700 text-right">PVA</TableHead>
                          <TableHead className="font-bold text-slate-700 text-right w-24">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {produtosByCategoria[cat]?.map((produto) => (
                          <TableRow 
                            key={produto.id} 
                            className="table-row-hover"
                            data-testid={`product-row-${produto.id}`}
                          >
                            <TableCell className="font-mono text-sm">{produto.codigo}</TableCell>
                            <TableCell className="font-medium">{produto.descricao}</TableCell>
                            <TableCell className="text-right">
                              <span className="font-mono font-semibold text-blue-600">
                                €{produto.pva.toFixed(2)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(produto)}
                                  className="text-slate-400 hover:text-blue-600"
                                  data-testid={`edit-product-${produto.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-slate-400 hover:text-red-500"
                                      data-testid={`delete-product-${produto.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Eliminar Produto?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Esta ação não pode ser revertida. O produto "{produto.descricao}" será permanentemente eliminado.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(produto.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Eliminar
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </TabsContent>
          ))}
        </Tabs>
      </Card>
    </div>
  );
}
